import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtCop, FamilyBadge, useCurrencyPref } from "./_shared";

/**
 * Admin sub-page: model behaviors.
 *
 * Lets an admin toggle which image-generation models are available and tune the
 * per-image COP cost for each (super_admin only). Also surfaces usage
 * distribution per model and the leaderboard per user so admins can spot
 * which models are loved / abused.
 */
export default function AdminModels() {
    const { user: me } = useAuth();
    const isSuper = me?.role === "super_admin";
    const [models, setModels] = useState([]);
    const [stats, setStats] = useState(null);
    const [saving, setSaving] = useState(false);
    // Currency is shared across admin pages via localStorage. The TRM still
    // comes from the backend so we don't drift from the canonical exchange rate.
    const [backendCurrency, setBackendCurrency] = useState("USD");
    const [currency, setCurrency] = useCurrencyPref(backendCurrency);
    const [trm, setTrm] = useState(4000);

    useEffect(() => {
        api.get("/admin/models").then((r) => setModels(r.data));
        api.get("/admin/stats").then((r) => setStats(r.data));
        api.get("/credits").then((r) => {
            setBackendCurrency(r.data.currency || "USD");
            setTrm(Number(r.data.trm_cop_per_usd || 4000));
        }).catch(() => {});
    }, []);

    /** Format a model's COP value as USD or COP based on the saved currency. */
    const fmtPrice = (cop) => {
        if (currency === "USD") {
            const usd = trm > 0 ? cop / trm : 0;
            return `$${usd.toLocaleString("es-CO", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 3,
            })} USD`;
        }
        return `${fmtCop(cop)} COP`;
    };

    const toggle = (i) => {
        setModels((arr) =>
            arr.map((m, idx) => (idx === i ? { ...m, enabled: !m.enabled } : m)),
        );
    };

    /**
     * Mark a single model as the platform-wide default. Only enabled models
     * can be defaulted (the chat would skip a disabled default anyway). The
     * UI shows a radio per row — selecting one auto-clears the others.
     */
    const setDefault = (i) => {
        setModels((arr) =>
            arr.map((m, idx) => ({
                ...m,
                is_default: idx === i ? !m.is_default : false,
            })),
        );
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/models", models);
            toast.success("Modelos actualizados");
            const r = await api.get("/admin/models");
            setModels(r.data);
        } catch {
            toast.error("Error guardando");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-10" data-testid="admin-models-page">
            <section>
                <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase">
                        Modelos de IA disponibles
                    </p>
                    <div className="flex items-center gap-2">
                        <div
                            className="inline-flex rounded-xl border hairline border-ink-600 bg-ink-900 p-0.5"
                            role="group"
                            data-testid="admin-models-currency-toggle"
                        >
                            {["USD", "COP"].map((c) => (
                                <button
                                    key={c}
                                    data-testid={`admin-models-currency-${c.toLowerCase()}`}
                                    onClick={() => setCurrency(c)}
                                    className={`px-3 py-1 rounded-lg text-[11px] font-mono tracking-widest uppercase transition-colors ${
                                        currency === c
                                            ? "bg-periwinkle-500 text-white"
                                            : "text-fog-400 hover:text-fog-100"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="admin-save-models-btn"
                            onClick={save}
                            disabled={saving}
                            className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 px-5 py-2 text-xs font-medium flex items-center gap-2"
                        >
                            <Save className="w-3.5 h-3.5" />
                            {saving ? "Guardando…" : "Guardar"}
                        </button>
                    </div>
                </div>
                <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 overflow-hidden divide-y divide-ink-600">
                    {models.map((m, i) => {
                        const copValue = m.cost_per_image_cop ?? 2000;
                        const usdValue = trm > 0 ? copValue / trm : 0;
                        // Super admin edits in the active currency. We keep
                        // COP as the canonical persisted field — when editing
                        // USD we recompute COP via TRM so backend stays consistent.
                        const editValue =
                            currency === "USD"
                                ? Number(usdValue.toFixed(4))
                                : copValue;
                        const altLabel =
                            currency === "USD"
                                ? `≈ ${fmtCop(copValue)} COP`
                                : `≈ $${usdValue.toFixed(3)} USD`;
                        return (
                            <div
                                key={m.id}
                                data-testid={`admin-model-${m.id}`}
                                className="flex items-center gap-4 px-5 py-4"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-fog-100 text-sm">{m.name}</p>
                                        <FamilyBadge family={m.family} />
                                        {m.requires_openai_key && (
                                            <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border hairline border-amber-400/40 text-amber-300">
                                                OpenAI key
                                            </span>
                                        )}
                                        {isSuper ? (
                                            <span className="inline-flex items-center gap-1 rounded-full border hairline border-periwinkle-500/40 bg-periwinkle-500/5 px-2 py-0.5">
                                                <span className="font-mono text-[10px] text-periwinkle-400">$</span>
                                                <input
                                                    data-testid={`admin-model-price-input-${m.id}`}
                                                    type="number"
                                                    min="0"
                                                    step={currency === "USD" ? "0.01" : "100"}
                                                    value={editValue}
                                                    onChange={(e) => {
                                                        const v = Number(e.target.value) || 0;
                                                        // Persist as COP (canonical). If editing
                                                        // USD, multiply by TRM and round to integer COP.
                                                        const newCop =
                                                            currency === "USD"
                                                                ? Math.round(v * trm)
                                                                : Math.round(v);
                                                        setModels((arr) =>
                                                            arr.map((x, j) =>
                                                                j === i
                                                                    ? {
                                                                          ...x,
                                                                          cost_per_image_cop: newCop,
                                                                      }
                                                                    : x,
                                                                ),
                                                            );
                                                    }}
                                                    className="w-24 bg-transparent text-periwinkle-400 text-xs font-mono focus:outline-none text-right"
                                                />
                                                <span className="font-mono text-[9px] text-fog-400 tracking-widest uppercase">
                                                    {currency} / img
                                                </span>
                                                <span
                                                    className="ml-1 font-mono text-[9px] text-fog-500 tracking-widest"
                                                    title={`TRM ${trm.toLocaleString("es-CO")} COP/USD`}
                                                >
                                                    {altLabel}
                                                </span>
                                            </span>
                                        ) : (
                                            <span
                                                className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border hairline border-periwinkle-500/40 text-periwinkle-400"
                                                data-testid={`admin-model-price-${m.id}`}
                                            >
                                                {fmtPrice(copValue)} / img
                                            </span>
                                        )}
                                    </div>
                                    <p className="font-mono text-[10px] text-fog-400 truncate mt-1">
                                        {m.id} · {m.type}
                                        {m.supports_image_input ? " · img ref" : ""}
                                    </p>
                                    {m.description && (
                                        <p className="text-fog-400 text-xs mt-1 leading-relaxed">
                                            {m.description}
                                        </p>
                                    )}
                                </div>
                                <label
                                    data-testid={`admin-default-${m.id}`}
                                    title={
                                        m.enabled
                                            ? "Marcar como modelo por defecto del chat"
                                            : "Habilita el modelo primero para poder marcarlo como predeterminado"
                                    }
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border hairline text-[10px] font-mono uppercase tracking-widest cursor-pointer transition-colors ${
                                        m.is_default
                                            ? "border-periwinkle-500 bg-periwinkle-500/10 text-periwinkle-400"
                                            : "border-ink-700 text-fog-500 hover:text-fog-300"
                                    } ${!m.enabled ? "opacity-40 cursor-not-allowed" : ""}`}
                                >
                                    <input
                                        type="radio"
                                        name="default-model"
                                        className="sr-only"
                                        disabled={!m.enabled}
                                        checked={!!m.is_default}
                                        onChange={() => m.enabled && setDefault(i)}
                                    />
                                    <span
                                        className={`w-3 h-3 rounded-full border ${
                                            m.is_default
                                                ? "border-periwinkle-500 bg-periwinkle-500"
                                                : "border-fog-500"
                                        }`}
                                    >
                                        {m.is_default && (
                                            <span className="block w-1.5 h-1.5 rounded-full bg-white m-0.5" />
                                        )}
                                    </span>
                                    {m.is_default ? "Predeterminado" : "Default"}
                                </label>
                                <button
                                    data-testid={`admin-toggle-${m.id}`}
                                    onClick={() => toggle(i)}
                                    className={`relative w-11 h-6 rounded-full transition-colors ${
                                        m.enabled ? "bg-periwinkle-500" : "bg-ink-700"
                                    }`}
                                >
                                    <span
                                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-ink-950 transition-transform ${
                                            m.enabled ? "translate-x-5" : "translate-x-0.5"
                                        }`}
                                    />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </section>

            {stats?.per_model?.length > 0 && (
                <section>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4">
                        Uso por modelo
                    </p>
                    <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 divide-y divide-ink-600">
                        {stats.per_model.map((r) => (
                            <div
                                key={r.model_id}
                                className="px-5 py-3 flex items-center justify-between"
                            >
                                <span className="text-sm text-fog-100 truncate">
                                    {r.name || r.model_id}
                                </span>
                                <span className="font-display text-lg text-periwinkle-500">
                                    {r.count}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {stats?.per_user?.length > 0 && (
                <section>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4">
                        Ranking por usuario
                    </p>
                    <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 divide-y divide-ink-600">
                        {stats.per_user.map((r) => (
                            <div
                                key={r.user_id || r.user || r.user_name}
                                className="px-5 py-3 flex items-center justify-between"
                            >
                                <span className="text-sm text-fog-100">
                                    {r.user_name || r.user || "—"}
                                </span>
                                <span className="font-display text-lg text-periwinkle-500">
                                    {r.count}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
